import React, { useState, useEffect, useRef } from 'react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import UserManagementSystem from './components/UserManagementSystem';
import AdvancedAnalyticsDashboard from './components/AdvancedAnalyticsDashboard';
import ChargingStationManagement from './components/ChargingStationManagement';
import BillingRevenueManagement from './components/BillingRevenueManagement';
import SecuritySettings from './components/SecuritySettings';
import SubscribersPage from './pages/SubscribersPage';
import OrdersPage from './pages/OrdersPage';
import CareersAdminPage from './pages/CareersAdminPage';
import EmailCampaignsPage from './pages/EmailCampaignsPage';
import ContactMessagesPage from './pages/ContactMessagesPage';
import B2BManagementPage from './pages/B2BManagementPage';
import NotificationSenderPage from './pages/NotificationSenderPage';
import HomeChargersPage from './pages/HomeChargersPage';
import ChargerIssuesPage from './pages/ChargerIssuesPage';
import { canAccess, isSuperAdmin } from './utils/permissions';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeComponent, setActiveComponent] = useState('dashboard');
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showManageMenu, setShowManageMenu] = useState(false);
  const [show2FABanner, setShow2FABanner] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('currentUser');
    if (token && user) {
      setIsAuthenticated(true);
      setCurrentUser(JSON.parse(user));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const check2FA = async () => {
        try {
          const token = localStorage.getItem('authToken');
          const response = await fetch('/api/auth/2fa/status', {
            headers: { 'Authorization': 'Bearer ' + token }
          });
          const data = await response.json();
          if (data.success && !data.enabled) {
            const dismissed = localStorage.getItem('2fa_banner_dismissed');
            if (!dismissed) setShow2FABanner(true);
          }
        } catch (e) {}
      };
      check2FA();
    }
  }, [isAuthenticated]);

  // Auto-logout
  useEffect(() => {
    let activityTimer, warningTimer, countdownTimer;
    const TIMEOUT = 10 * 60 * 1000;
    const WARNING = 60 * 1000;

    const resetTimer = () => {
      clearTimeout(activityTimer); clearTimeout(warningTimer); clearInterval(countdownTimer);
      setShowTimeoutWarning(false);
      if (isAuthenticated) {
        warningTimer = setTimeout(() => {
          setShowTimeoutWarning(true); setTimeoutCountdown(60);
          countdownTimer = setInterval(() => {
            setTimeoutCountdown(prev => { if (prev <= 1) { doLogout(); return 0; } return prev - 1; });
          }, 1000);
        }, TIMEOUT - WARNING);
        activityTimer = setTimeout(doLogout, TIMEOUT);
      }
    };
    const doLogout = () => {
      clearTimeout(activityTimer); clearTimeout(warningTimer); clearInterval(countdownTimer);
      setShowTimeoutWarning(false);
      localStorage.removeItem('authToken'); localStorage.removeItem('currentUser');
      setIsAuthenticated(false); setCurrentUser(null);
      alert('You have been logged out due to inactivity.');
    };
    const handleActivity = () => { if (isAuthenticated) resetTimer(); };
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    if (isAuthenticated) { events.forEach(e => document.addEventListener(e, handleActivity, true)); resetTimer(); }
    return () => { events.forEach(e => document.removeEventListener(e, handleActivity, true)); clearTimeout(activityTimer); clearTimeout(warningTimer); clearInterval(countdownTimer); };
  }, [isAuthenticated]);

  const handleLogin = (user, token) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('currentUser', JSON.stringify(user));
    setCurrentUser(user); setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken'); localStorage.removeItem('token');
    localStorage.removeItem('currentUser'); localStorage.removeItem('2fa_banner_dismissed');
    setCurrentUser(null); setIsAuthenticated(false); setActiveComponent('dashboard');
    setShowUserMenu(false); setShowManageMenu(false); setShow2FABanner(false);
  };

  const navTo = (comp) => {
    setActiveComponent(comp); setShowManageMenu(false); setShowUserMenu(false); setMobileMenuOpen(false);
  };

  const superAdmin = isSuperAdmin(currentUser);
  // manageItems with b2b
const manageItems = ['subscribers', 'orders', 'careers', 'emails', 'b2b', 'notifications', 'messages', 'home-chargers'];
  const isManageActive = manageItems.includes(activeComponent);
  // Check if user can see any items in the Manage dropdown
  const canSeeManage = manageItems.some(p => canAccess(currentUser, p));

  const renderComponent = () => {
    switch (activeComponent) {
      case 'dashboard': return <Dashboard user={currentUser} setActiveComponent={setActiveComponent} />;
      case 'users': return <UserManagementSystem user={currentUser} />;
      case 'analytics': return <AdvancedAnalyticsDashboard user={currentUser} />;
      case 'stations': return <ChargingStationManagement user={currentUser} />;
      case 'issues': return <ChargerIssuesPage user={currentUser} />;
      case 'billing': return <BillingRevenueManagement user={currentUser} />;
      case 'security': return <SecuritySettings user={currentUser} />;
      case 'subscribers': return <SubscribersPage />;
      case 'orders': return <OrdersPage />;
      case 'careers': return <CareersAdminPage />;
      case 'emails': return <EmailCampaignsPage />;
      case 'messages': return <ContactMessagesPage user={currentUser} />;
      case 'b2b': return <B2BManagementPage user={currentUser} />;
      case 'notifications': return <NotificationSenderPage user={currentUser} />;
      case 'home-chargers': return <HomeChargersPage user={currentUser} />;
      default: return <Dashboard user={currentUser} setActiveComponent={setActiveComponent} />;
    }
  };

  if (!isAuthenticated) return <Login onLogin={handleLogin} />;

  // Nav button — only shows if user can access the page
  const NavBtn = ({ id, icon, label }) => {
    if (!canAccess(currentUser, id)) return null;
    return (
      <button onClick={() => navTo(id)} className={`flex items-center space-x-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${activeComponent === id ? 'bg-green-100 text-green-700 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
        <span>{icon}</span><span>{label}</span>
      </button>
    );
  };

  const MobileBtn = ({ id, icon, label }) => {
    if (!canAccess(currentUser, id)) return null;
    return (
      <button onClick={() => navTo(id)} className={`flex items-center space-x-3 w-full text-left px-3 py-2 rounded-md text-base font-medium ${activeComponent === id ? 'bg-green-100 text-green-700' : 'text-gray-600 hover:bg-gray-100'}`}>
        <span>{icon}</span><span>{label}</span>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">

            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <img src="https://lmpenergy.co.uk/pics/Logo.png" alt="LMPEnergy" className="w-10 h-10 rounded-full mr-3"
                  onError={(e) => { e.target.outerHTML = '<div class="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-full mr-3 flex items-center justify-center text-white font-bold text-sm shadow-lg">LMP</div>'; }}
                />
                <h1 className="text-xl font-bold text-gray-900">LMPEnergy</h1>
                <span className="ml-2 text-sm text-gray-500 font-medium hidden lg:inline">LMPEnergy Admin</span>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100">
                <svg className={`${mobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                <svg className={`${mobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center space-x-1">
              <NavBtn id="dashboard" icon="🏠" label="Dashboard" />
              <NavBtn id="analytics" icon="📊" label="Analytics" />
              <NavBtn id="users" icon="👥" label="Users" />
              <NavBtn id="stations" icon="⚡" label="Stations" />
              <NavBtn id="issues" icon="🚩" label="Issues" />
              <NavBtn id="billing" icon="💰" label="Billing" />

              {/* Management dropdown — only if user can see at least one manage page */}
              {canSeeManage && (
                <div className="relative">
                  <button
                    onClick={() => { setShowManageMenu(!showManageMenu); setShowUserMenu(false); }}
                    className={`flex items-center space-x-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${isManageActive ? 'bg-green-100 text-green-700 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                  >
                    <span>📋</span><span>Manage</span>
                    <svg className={`w-3.5 h-3.5 ml-0.5 transition-transform ${showManageMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  {showManageMenu && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {canAccess(currentUser, 'subscribers') && (
                        <button onClick={() => navTo('subscribers')} className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${activeComponent === 'subscribers' ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="mr-3">📧</span> Mailing List
                        </button>
                      )}
                      {canAccess(currentUser, 'orders') && (
                        <button onClick={() => navTo('orders')} className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${activeComponent === 'orders' ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="mr-3">🛒</span> Orders
                        </button>
                      )}
                      {canAccess(currentUser, 'careers') && (
                        <button onClick={() => navTo('careers')} className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${activeComponent === 'careers' ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="mr-3">💼</span> Careers
                        </button>
                      )}
                      {canAccess(currentUser, 'emails') && (
                        <button onClick={() => navTo('emails')} className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${activeComponent === 'emails' ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="mr-3">📤</span> Email Campaigns
                        </button>
                      )}
                      {canAccess(currentUser, 'messages') && (
                        <button onClick={() => navTo('messages')} className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${activeComponent === 'messages' ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="mr-3">✉️</span> Messages
                        </button>
                      )}
                      {canAccess(currentUser, 'home-chargers') && (
                        <button onClick={() => navTo('home-chargers')} className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${activeComponent === 'home-chargers' ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="mr-3">🔌</span> Home Chargers
                        </button>
                      )}
                      {canAccess(currentUser, 'b2b') && (
                        <button onClick={() => navTo('b2b')} className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${activeComponent === 'b2b' ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="mr-2">🏢</span> B2B Companies
                        </button>
                      )}
                      {canAccess(currentUser, 'notifications') && (
                        <button onClick={() => navTo('notifications')} className={`flex items-center w-full px-4 py-2.5 text-sm transition-colors ${activeComponent === 'notifications' ? 'bg-green-50 text-green-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <span className="mr-2">🔔</span> Notifications
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </nav>

            {/* User dropdown */}
            <div className="hidden md:flex items-center relative">
              <button
                onClick={() => { setShowUserMenu(!showUserMenu); setShowManageMenu(false); }}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {currentUser?.firstName?.[0]}{currentUser?.lastName?.[0]}
                </div>
                <span className="text-sm font-medium text-gray-700 hidden lg:inline">{currentUser?.firstName}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{currentUser?.firstName} {currentUser?.lastName}</p>
                    <p className="text-xs text-gray-500">{currentUser?.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full capitalize">
                      {currentUser?.role?.replace('_', ' ')}
                    </span>
                  </div>
                  <button onClick={() => navTo('security')} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    <span className="mr-2">🔒</span> Security Settings
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button onClick={handleLogout} className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                    <span className="mr-2">🚪</span> Logout
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu */}
          <div className={`md:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}>
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center px-3 py-2 mb-2">
                <span className="text-sm font-medium text-gray-800">{currentUser?.firstName} {currentUser?.lastName}</span>
                <span className="ml-3 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full capitalize">{currentUser?.role?.replace('_', ' ')}</span>
              </div>

              <MobileBtn id="dashboard" icon="🏠" label="Dashboard" />
              <MobileBtn id="analytics" icon="📊" label="Analytics" />
              <MobileBtn id="users" icon="👥" label="Users" />
              <MobileBtn id="stations" icon="⚡" label="Stations" />
              <MobileBtn id="issues" icon="🚩" label="Issues" />
              <MobileBtn id="billing" icon="💰" label="Billing" />

              {canSeeManage && (
                <>
                  <div className="border-t border-gray-200 mt-2 pt-2">
                    <p className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Management</p>
                  </div>
                  <MobileBtn id="subscribers" icon="📧" label="Mailing List" />
                  <MobileBtn id="orders" icon="🛒" label="Orders" />
                  <MobileBtn id="careers" icon="💼" label="Careers" />
                  <MobileBtn id="emails" icon="📤" label="Email Campaigns" />
                  <MobileBtn id="messages" icon="✉️" label="Messages" />
                  <MobileBtn id="home-chargers" icon="🔌" label="Home Chargers" />
                  <MobileBtn id="b2b" icon="🏢" label="B2B Companies" />
                  <MobileBtn id="notifications" icon="🔔" label="Notifications" />
                </>
              )}

              <button onClick={() => navTo('security')} className={`flex items-center space-x-3 w-full text-left px-3 py-2 rounded-md text-base font-medium ${activeComponent === 'security' ? 'bg-green-100 text-green-700' : 'text-gray-600 hover:bg-gray-100'}`}>
                <span>🔒</span><span>Security</span>
              </button>
              <button onClick={handleLogout} className="flex items-center space-x-3 w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 mt-2 border-t border-gray-200 pt-4">
                <span>🚪</span><span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 2FA Banner */}
      {show2FABanner && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-lg mr-3">🔐</span>
              <div>
                <p className="text-sm font-medium text-amber-900">Secure your account with two-factor authentication</p>
                <p className="text-xs text-amber-700">Add an extra layer of security using an authenticator app</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={() => { navTo('security'); setShow2FABanner(false); }} className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700">Set up now</button>
              <button onClick={() => { setShow2FABanner(false); localStorage.setItem('2fa_banner_dismissed', 'true'); }} className="px-3 py-1.5 text-sm font-medium text-amber-700 hover:text-amber-900">Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {(showUserMenu || showManageMenu) && (
        <div className="fixed inset-0 z-40" onClick={() => { setShowUserMenu(false); setShowManageMenu(false); }}></div>
      )}

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          {renderComponent()}
        </div>
      </main>

      {showTimeoutWarning && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000 }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)', maxWidth: '400px' }}>
            <h3 style={{ color: '#DC2626', marginBottom: '15px' }}>Session Timeout Warning</h3>
            <p style={{ marginBottom: '20px' }}>You will be logged out in <strong>{timeoutCountdown}</strong> seconds due to inactivity.</p>
            <button onClick={() => setShowTimeoutWarning(false)} style={{ backgroundColor: '#10B981', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Stay Logged In</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
